import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import api, { formatApiError } from "@/lib/api";
import { Trash2, ShoppingBag, Truck, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FREE_SHIPPING_AT = 1999;
const MIN_ORDER = 1000;

export default function Cart() {
  const { items, updateQty, removeFromCart, subtotal } = useCart();
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const nav = useNavigate();

  const applyCoupon = async () => {
    if (!code) return;
    try {
      const { data } = await api.post("/coupon/apply", { code, subtotal });
      setApplied(data);
      toast.success(`Coupon ${data.code} applied — ₹${data.discount} off`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const discount = applied?.discount || 0;
  const shipping = subtotal >= FREE_SHIPPING_AT || subtotal === 0 ? 0 : 79;
  const total = Math.max(0, subtotal - discount + shipping);
  const belowMin = subtotal > 0 && subtotal < MIN_ORDER;
  const toFreeShip = Math.max(0, FREE_SHIPPING_AT - subtotal);
  const progressPct = Math.min(100, Math.round((subtotal / FREE_SHIPPING_AT) * 100));

  return (
    <div className="container-x py-10" data-testid="cart-page">
      <h1 className="font-serif text-4xl md:text-5xl mb-8">Your Cart</h1>
      {items.length === 0 ? (
        <div className="border hairline rounded-xl p-16 text-center bg-white" data-testid="empty-cart">
          <ShoppingBag className="w-10 h-10 mx-auto gold-text" strokeWidth={1.25} />
          <div className="font-serif text-2xl mt-4">Your cart is empty</div>
          <p className="text-sm text-muted-foreground mt-2">Discover our handcrafted collection.</p>
          <Link to="/shop" className="inline-block mt-6 btn-gold rounded-full px-8 py-3 text-xs uppercase tracking-widest">Start Shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="space-y-4">
            {items.map((it) => {
              const lineId = it.line_id;
              const testKey = [it.product_id, it.size?.replace(/\s+/g, "-"), it.design?.replace(/\s+/g, "-")].filter(Boolean).join("-");
              return (
              <div key={lineId} className="flex gap-4 border hairline rounded-xl p-4 bg-white" data-testid={`cart-item-${testKey}`}>
                <img src={it.image} alt={it.name} className="w-24 h-24 rounded object-cover bg-muted" />
                <div className="flex-1">
                  <Link to={`/product/${it.slug}`} className="font-serif text-lg hover:gold-text">{it.name}</Link>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {it.size && (
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted-foreground border hairline rounded-full px-2 py-0.5" data-testid={`cart-size-${testKey}`}>
                        Size · <span className="text-espresso font-medium">{it.size}</span>
                      </span>
                    )}
                    {it.design && (
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted-foreground border hairline rounded-full px-2 py-0.5" data-testid={`cart-design-${testKey}`}>
                        Design · <span className="text-espresso font-medium">{it.design}</span>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">₹{it.price}{it.unit_label ? ` ${it.unit_label}` : " each"}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center border hairline rounded-full">
                      <button onClick={() => updateQty(lineId, it.quantity - 1)} className="px-3 py-1" data-testid={`cart-minus-${testKey}`}>−</button>
                      <div className="px-3 text-sm">{it.quantity}</div>
                      <button onClick={() => updateQty(lineId, it.quantity + 1)} className="px-3 py-1" data-testid={`cart-plus-${testKey}`}>+</button>
                    </div>
                    <button onClick={() => removeFromCart(lineId)} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid={`cart-remove-${testKey}`}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Remove
                    </button>
                  </div>
                </div>
                <div className="font-serif text-lg">₹{it.price * it.quantity}</div>
              </div>
              );
            })}
          </div>
          <aside className="border hairline rounded-xl p-6 bg-white h-fit sticky top-40" data-testid="cart-summary">
            <div className="font-serif text-2xl mb-4">Order Summary</div>

            {/* Free shipping progress */}
            <div className="mb-4 border hairline rounded-lg p-3 bg-muted/30" data-testid="free-shipping-progress">
              {subtotal >= FREE_SHIPPING_AT ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-medium">Congrats! You've unlocked FREE shipping.</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs text-espresso">
                    <Truck className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} />
                    <span>Add <span className="font-medium text-espresso">₹{toFreeShip}</span> more to unlock <span className="font-medium">FREE shipping</span>.</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-white border hairline overflow-hidden">
                    <div className="h-full bg-[#B8860B] transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="progress-fill"></div>
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground text-right">₹{subtotal} of ₹{FREE_SHIPPING_AT}</div>
                </>
              )}
            </div>

            {belowMin && (
              <div className="mb-4 border border-amber-300 bg-amber-50/60 rounded-lg p-3 text-xs text-amber-800" data-testid="min-order-notice">
                Minimum order value is <span className="font-medium">₹{MIN_ORDER}</span>. Please add <span className="font-medium">₹{MIN_ORDER - subtotal}</span> more to proceed.
              </div>
            )}

            <div className="flex gap-2 mb-4">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Coupon code" className="flex-1 border hairline rounded-full px-4 py-2 text-sm" data-testid="coupon-input" />
              <button onClick={applyCoupon} className="btn-outline-gold rounded-full px-4 text-xs uppercase" data-testid="coupon-apply">Apply</button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">₹{subtotal}</span></div>
              {applied && <div className="flex justify-between text-emerald-700"><span>Discount ({applied.code})</span><span>−₹{discount}</span></div>}
              <div className="flex justify-between"><span>Shipping</span><span>{shipping ? `₹${shipping}` : "Free"}</span></div>
              <div className="border-t hairline pt-3 flex justify-between font-serif text-xl"><span>Total</span><span className="gold-text" data-testid="summary-total">₹{total}</span></div>
            </div>
            <button
              onClick={() => nav("/checkout", { state: { couponCode: applied?.code } })}
              disabled={belowMin}
              className={`mt-5 w-full rounded-full py-3 text-xs uppercase tracking-widest ${belowMin ? "bg-muted text-muted-foreground cursor-not-allowed" : "btn-gold"}`}
              data-testid="cart-checkout"
            >
              {belowMin ? `Add ₹${MIN_ORDER - subtotal} more to checkout` : "Checkout — Prepaid UPI"}
            </button>
            <Link to="/shop" className="mt-3 block text-center text-xs text-muted-foreground hover:gold-text">Continue shopping</Link>
            <div className="mt-3 text-[11px] text-muted-foreground text-center">Prepaid orders only · Min order ₹{MIN_ORDER} · Free shipping ₹{FREE_SHIPPING_AT}+</div>
          </aside>
        </div>
      )}
    </div>
  );
}
